import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Alert,
  MenuItem,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import api from '../services/api';

const EditContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    contract_number: '',
    contract_type: 'temporary',
    position_title: '',
    position_title_lt: '',
    work_location: '',
    work_location_country: 'LT',
    start_date: '',
    end_date: '',
    probation_period_months: 3,
    salary_amount: '',
    salary_currency: 'EUR',
    salary_type: 'monthly',
    working_hours_per_week: 40,
    vacation_days: 20,
    notice_period_days: 14,
    client_company_name: '',
    client_company_code: '',
    client_company_address: '',
    signed_by_employee: false,
    signed_by_employer: false,
    employee_signed_date: '',
    employer_signed_date: '',
    status: 'draft',
    notes: ''
  });

  const commonPositions = [
    { en: 'Warehouse Worker', lt: 'Sandėlio darbuotojas' },
    { en: 'Production Worker', lt: 'Gamybos darbuotojas' },
    { en: 'Cleaner', lt: 'Valytojas' },
    { en: 'Construction Worker', lt: 'Statybininkas' },
    { en: 'Driver', lt: 'Vairuotojas' },
    { en: 'Forklift Operator', lt: 'Krautuvo vairuotojas' },
    { en: 'Packer', lt: 'Pakuotojas' },
    { en: 'Assembly Worker', lt: 'Surinkėjas' },
    { en: 'Kitchen Helper', lt: 'Virtuvės pagalbininkas' },
    { en: 'Agricultural Worker', lt: 'Žemės ūkio darbuotojas' }
  ];

  useEffect(() => {
    fetchEmployees();
    fetchContract();
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const activeEmployees = response.data.filter(emp => emp.status === 'active');
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchContract = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${id}`);
      const contract = response.data;
      
      // Format dates for input fields
      if (contract.start_date) {
        contract.start_date = contract.start_date.split('T')[0];
      }
      if (contract.end_date) {
        contract.end_date = contract.end_date.split('T')[0];
      }
      if (contract.employee_signed_date) {
        contract.employee_signed_date = contract.employee_signed_date.split('T')[0];
      }
      if (contract.employer_signed_date) {
        contract.employer_signed_date = contract.employer_signed_date.split('T')[0];
      }
      
      setFormData(contract);
      
      // Set selected employee
      const employee = employees.find(emp => emp.id === contract.employee_id);
      if (employee) {
        setSelectedEmployee(employee);
      }
      
      setError('');
    } catch (error) {
      console.error('Error fetching contract:', error);
      setError('Failed to load contract data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePositionSelect = (position) => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        position_title: position.en,
        position_title_lt: position.lt
      }));
    }
  };

  const handleEmployeeChange = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        employee_id: newValue.id
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.employee_id) {
      setError('Please select an employee');
      return;
    }

    if (!formData.position_title || !formData.start_date || !formData.end_date) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.salary_amount || formData.salary_amount <= 0) {
      setError('Please enter a valid salary amount');
      return;
    }

    try {
      const updateData = { ...formData };
      // Remove read-only fields
      delete updateData.id;
      delete updateData.created_at;
      delete updateData.updated_at;
      delete updateData.employee_first_name;
      delete updateData.employee_last_name;
      delete updateData.employee_personal_code;
      
      await api.put(`/contracts/${id}`, updateData);
      setSuccess('Contract updated successfully!');
      
      setTimeout(() => {
        navigate(`/admin/contracts/${id}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating contract:', error);
      setError(error.response?.data?.error || 'Failed to update contract');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading contract data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(`/admin/contracts/${id}`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Edit Contract: {formData.contract_number}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Contract Basic Information */}
          <Typography variant="h6" gutterBottom>
            Contract Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contract Number"
                name="contract_number"
                value={formData.contract_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Contract Type"
                name="contract_type"
                value={formData.contract_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="temporary">Temporary Employment</MenuItem>
                <MenuItem value="permanent">Permanent Employment</MenuItem>
                <MenuItem value="seasonal">Seasonal Work</MenuItem>
                <MenuItem value="project">Project Based</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status || 'draft'}
                onChange={handleChange}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Employee Information - Read Only */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Employee Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee"
                value={`${formData.employee_first_name || ''} ${formData.employee_last_name || ''} (${formData.employee_personal_code || ''})`}
                disabled
                helperText="Employee cannot be changed after contract creation"
              />
            </Grid>
          </Grid>

          {/* Position Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Position Details
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={commonPositions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.en}
                value={formData.position_title}
                onChange={(event, value) => handlePositionSelect(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position Title (English)"
                    name="position_title"
                    value={formData.position_title}
                    onChange={handleChange}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position Title (Lithuanian)"
                name="position_title_lt"
                value={formData.position_title_lt || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Work Location"
                name="work_location"
                value={formData.work_location || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Work Country"
                name="work_location_country"
                value={formData.work_location_country || 'LT'}
                onChange={handleChange}
                required
              >
                <MenuItem value="LT">Lithuania</MenuItem>
                <MenuItem value="DE">Germany</MenuItem>
                <MenuItem value="NL">Netherlands</MenuItem>
                <MenuItem value="BE">Belgium</MenuItem>
                <MenuItem value="PL">Poland</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Contract Duration */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Contract Duration
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Probation Period (months)"
                name="probation_period_months"
                type="number"
                value={formData.probation_period_months || 0}
                onChange={handleChange}
                inputProps={{ min: 0, max: 6 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Notice Period (days)"
                name="notice_period_days"
                type="number"
                value={formData.notice_period_days || 14}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          {/* Salary Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Compensation
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Salary Amount"
                name="salary_amount"
                type="number"
                value={formData.salary_amount || ''}
                onChange={handleChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Currency"
                name="salary_currency"
                value={formData.salary_currency || 'EUR'}
                onChange={handleChange}
                required
              >
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Salary Type"
                name="salary_type"
                value={formData.salary_type || 'monthly'}
                onChange={handleChange}
                required
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Hours/Week"
                name="working_hours_per_week"
                type="number"
                value={formData.working_hours_per_week || 40}
                onChange={handleChange}
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Vacation Days/Year"
                name="vacation_days"
                type="number"
                value={formData.vacation_days || 20}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          {/* Client Company */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Client Company (Optional)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Client Company Name"
                name="client_company_name"
                value={formData.client_company_name || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Code"
                name="client_company_code"
                value={formData.client_company_code || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Address"
                name="client_company_address"
                value={formData.client_company_address || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Signature Status */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Signature Status
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="signed_by_employee"
                    checked={formData.signed_by_employee || false}
                    onChange={handleChange}
                  />
                }
                label="Signed by Employee"
              />
              {formData.signed_by_employee && (
                <TextField
                  fullWidth
                  label="Employee Signed Date"
                  name="employee_signed_date"
                  type="date"
                  value={formData.employee_signed_date || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="signed_by_employer"
                    checked={formData.signed_by_employer || false}
                    onChange={handleChange}
                  />
                }
                label="Signed by Employer"
              />
              {formData.signed_by_employer && (
                <TextField
                  fullWidth
                  label="Employer Signed Date"
                  name="employer_signed_date"
                  type="date"
                  value={formData.employer_signed_date || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
          </Grid>

          {/* Notes */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Additional Notes
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate(`/admin/contracts/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditContract;
